import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/hooks/useAuth';

export interface MenuItem {
  label: string;
  path: string;
  /** trueの場合、raceAliasプレフィックスなしで遷移 */
  absolute?: boolean;
  /** メニューアイコン */
  icon?: ReactNode;
}

export interface DrawerMenuProps {
  open: boolean;
  onClose: () => void;
  /** ナビゲーション項目（ログイン時のみ表示） */
  menuItems: MenuItem[];
  /** 大会エイリアス（リンク生成用） */
  raceAlias: string;
}

const footerLinks: MenuItem[] = [
  { label: '運営会社', path: 'https://swandive.co.jp', absolute: true },
  { label: '利用規約', path: 'https://2dot99.com/tos.html', absolute: true },
  { label: 'プライバシーポリシー', path: 'https://2dot99.com/privacypolicy.html', absolute: true },
];

export function DrawerMenu({ open, onClose, menuItems, raceAlias }: DrawerMenuProps) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const handleNavigate = (item: MenuItem) => {
    // 外部リンクの場合は新しいタブで開く
    if (item.path.startsWith('http')) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
      onClose();
      return;
    }
    const path = item.absolute ? item.path : `/${raceAlias}${item.path}`;
    navigate(path);
    onClose();
  };

  const handleSignOut = async () => {
    await signOut();
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 280 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={onClose} aria-label="メニューを閉じる">
            <CloseIcon />
          </IconButton>
        </Box>

        {user && (
          <>
            <List sx={{ px: 1 }}>
              {menuItems.map((item) => (
                <ListItemButton key={item.path} onClick={() => handleNavigate(item)}>
                  {item.icon && <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>{item.icon}</ListItemIcon>}
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>

            <List sx={{ mt: 2, px: 1 }}>
              <ListItemButton onClick={handleSignOut}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="ログアウト" />
              </ListItemButton>
            </List>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        <List dense sx={{ px: 1 }}>
          {footerLinks.map((item) => (
            <ListItemButton key={item.path} onClick={() => handleNavigate(item)}>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </ListItemButton>
          ))}
        </List>

        {raceAlias === 'ehime2026' && (
          <List dense sx={{ px: 1, mt: 2 }}>
            <Box sx={{ pl: 2, pb: 0.5, color: 'text.secondary', typography: 'body2' }}>過去大会の完走証</Box>
            <ListItemButton
              component="a"
              href="https://ehime.2dot99.com/2024"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ListItemText
                primary="第61回愛媛マラソン（2024年）"
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </ListItemButton>
            <ListItemButton component="a" href="https://ehime.2dot99.com/" target="_blank" rel="noopener noreferrer">
              <ListItemText
                primary="第62回愛媛マラソン（2025年）"
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </ListItemButton>
          </List>
        )}
      </Box>
    </Drawer>
  );
}
